// "New Handover" dialog (nursing-ops sprint, Epic 9 / D11). Creates a draft
// ShiftHandover; the backend auto-populates one SBAR row per admitted
// patient on the chosen ward (main/ipd/manage_shift_handover.py post()).
//
// A nurse hands over only a ward they worked: the Ward list is the wards they
// are rostered to (on duty / overtime) on the chosen date for the chosen OUTGOING
// shift — not the shift in progress, which has already rolled over by the time
// most handovers happen (QA ND #7). So the outgoing shift is picked first.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { createShiftHandover, getMyRoster, listOf } from "../api/nurseApi";
import { useShiftSchedule } from "../hooks";

const WORKING = new Set(["on_duty", "overtime"]);


function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const EMPTY = { shiftDate: todayStr(), outgoingShift: "", incomingShift: "", wardCode: "" };

export default function ShiftHandoverCreateDialog({ open, onClose, wards, handedOverBy, onCreated }) {
  const { schedule } = useShiftSchedule();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // The nurse's own roster rows for the chosen date; null while loading,
  // "error" if the fetch failed — never a silent empty list.
  const [myRows, setMyRows] = useState(null);
  useEffect(() => {
    if (!open || !form.shiftDate) return undefined;
    let alive = true;
    setMyRows(null);
    getMyRoster({ from_date: form.shiftDate, to_date: form.shiftDate })
      .then((r) => { if (alive) setMyRows(listOf(r)); })
      .catch(() => { if (alive) setMyRows("error"); });
    return () => { alive = false; };
  }, [open, form.shiftDate]);

  const wardOptions = useMemo(() => {
    if (!Array.isArray(myRows) || !form.outgoingShift) return [];
    const nameByCode = new Map(wards.map((w) => [w.code, w.name]));
    const codes = new Set(myRows
      .filter((r) => r.roster_date === form.shiftDate && r.shift === form.outgoingShift && WORKING.has(r.status))
      .map((r) => r.ward_code));
    return Array.from(codes, (code) => ({ code, name: nameByCode.get(code) || code }));
  }, [myRows, wards, form.shiftDate, form.outgoingShift]);

  // A ward chosen for another date or shift is no longer one the nurse worked.
  useEffect(() => {
    if (form.wardCode && !wardOptions.some((w) => w.code === form.wardCode)) setForm((f) => ({ ...f, wardCode: "" }));
  }, [wardOptions, form.wardCode]);

  let wardHint = null;
  if (!form.outgoingShift) wardHint = "Choose the outgoing shift first";
  else if (myRows === null) wardHint = "Loading your roster…";
  else if (myRows === "error") wardHint = "Could not load your roster. Please try again.";
  else if (wardOptions.length === 0) wardHint = "You are not rostered on any ward for this shift";

  const canSubmit =
    form.shiftDate && form.outgoingShift && form.incomingShift && form.wardCode && handedOverBy && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await createShiftHandover({
        handed_over_by: handedOverBy,
        shift_date: form.shiftDate,
        outgoing_shift: form.outgoingShift,
        incoming_shift: form.incomingShift,
        ward_id: form.wardCode,
      });
      if (res?.status !== "Success") {
        toast.error(res?.message || "Failed to create handover");
        return;
      }
      toast.success(res?.message || "Handover created");
      setForm(EMPTY);
      onCreated?.(res.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create handover");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NURSE_COLORS.primary }}>New Shift Handover</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            type="date" size="small" label="Shift date" value={form.shiftDate}
            onChange={(e) => set({ shiftDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select size="small" label="Outgoing shift" value={form.outgoingShift}
            onChange={(e) => set({ outgoingShift: e.target.value })}
          >
            {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
          </TextField>
          <TextField
            select size="small" label="Ward" value={form.wardCode}
            onChange={(e) => set({ wardCode: e.target.value })}
            helperText={wardHint || undefined}
          >
            {wardOptions.map((w) => (<MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>))}
            {wardOptions.length === 0 && <MenuItem value="" disabled>{wardHint}</MenuItem>}
          </TextField>
          <TextField
            select size="small" label="Incoming shift" value={form.incomingShift}
            onChange={(e) => set({ incomingShift: e.target.value })}
          >
            {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
          </TextField>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Handing over as: {handedOverBy || "—"}
            </Typography>
          </Box>
          {!handedOverBy && <Alert severity="warning">Could not resolve your nurse ID — cannot create a handover.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained">
          {submitting ? "Creating..." : "Create Handover"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
